/**
 * Settings Screen - MOBILE ONLY
 * 
 * User preferences and account settings.
 * Admin link appears here (role-gated).
 * 
 * Structure (aligned with Home):
 * ScreenContainer
 * └── ScrollView
 *     ├── Header (Title)
 *     ├── Account section
 *     ├── Admin section (role-gated)
 *     └── Sign Out section
 */

import React from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { color, text, space, radius, touch, icon, layout, border } from '../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard, ListRow } from '../../components/v2/mobile';

// =============================================================================
// SCREEN
// =============================================================================

export default function SettingsScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  
  const isAdmin = user?.role === 'admin';
  const userEmail = user?.email || 'User';
  const userName = user?.display_name || userEmail.split('@')[0];

  const handleLogout = async () => {
    await clearAuth();
    router.replace('/(auth)/login' as any);
  };

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>Settings</Text>
        </View>

        {/* PROFILE SUMMARY */}
        <View style={styles.section}>
          <ListCard>
            <Pressable 
              style={styles.profileRow}
              onPress={() => router.push('/v2/settings/profile' as any)}
            >
              <View style={styles.avatar}>
                <Text style={styles.avatarInitial}>
                  {userName.charAt(0).toUpperCase()}
                </Text>
              </View>
              <View style={styles.profileContent}>
                <Text style={styles.profileName}>{userName}</Text>
                <Text style={styles.profileEmail}>{userEmail}</Text>
              </View>
              <Ionicons name="chevron-forward" size={icon.sm + 2} color={color.textMuted} />
            </Pressable>
          </ListCard>
        </View>

        {/* ACCOUNT */}
        <View style={styles.section}>
          <SectionHeader label="Account" />
          <ListCard>
            <ListRow
              iconName="person-outline"
              title="Profile"
              onPress={() => router.push('/v2/settings/profile' as any)}
            />
            <ListRow
              iconName="lock-closed-outline"
              title="Change Password"
              onPress={() => router.push('/v2/settings/security' as any)}
            />
            <ListRow
              iconName="notifications-outline"
              title="Notifications"
              onPress={() => router.push('/v2/settings/notifications' as any)}
            />
            <ListRow
              iconName="moon-outline"
              title="Appearance"
              onPress={() => router.push('/v2/settings/appearance' as any)}
              hideBorder
            />
          </ListCard>
        </View>

        {/* APP INFO */}
        <View style={styles.section}>
          <SectionHeader label="App" />
          <ListCard>
            <ListRow
              iconName="help-circle-outline"
              title="Help & Support"
              onPress={() => router.push('/v2/settings/help' as any)}
            />
            <ListRow
              iconName="document-text-outline"
              title="Terms of Service"
              onPress={() => router.push('/v2/settings/terms' as any)}
            />
            <ListRow
              iconName="shield-checkmark-outline"
              title="Privacy Policy"
              onPress={() => router.push('/v2/settings/privacy' as any)}
              hideBorder
            />
          </ListCard>
        </View>

        {/* ADMIN (role-gated) */}
        {isAdmin && (
          <View style={styles.section}>
            <SectionHeader label="Admin" />
            <ListCard>
              <ListRow
                iconName="shield-outline"
                iconColor={color.primary}
                title="Admin Panel"
                subtitle="Manage users, requests, and system settings"
                onPress={() => router.push('/v2/admin' as any)}
                hideBorder
              />
            </ListCard>
          </View>
        )}

        {/* SIGN OUT */}
        <View style={styles.section}>
          <ListCard>
            <Pressable
              onPress={handleLogout}
              style={({ pressed }) => [
                styles.signOutRow,
                pressed && styles.pressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Sign out"
            >
              <Ionicons name="log-out-outline" size={icon.md} color={color.error} />
              <Text style={styles.signOutText}>Sign Out</Text>
            </Pressable>
          </ListCard>
        </View>

        {/* VERSION */}
        <View style={styles.versionContainer}>
          <Text style={styles.versionText}>Version 2.0.0</Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// STYLES - Aligned with Home screen
// =============================================================================

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: space.md,
    paddingTop: space.md,
    paddingBottom: layout.scrollBottomPadding,
  },
  header: {
    marginBottom: space.lg,
  },
  title: {
    fontSize: text.pageTitle.fontSize,
    fontWeight: text.pageTitle.fontWeight,
    lineHeight: text.pageTitle.lineHeight,
    letterSpacing: text.pageTitle.letterSpacing,
    color: color.textPrimary,
  },
  section: {
    marginBottom: space.lg,
  },

  // Profile row
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touch.listRow + space.md,
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    gap: space.sm + space.xs,
    backgroundColor: color.surface,
  },
  avatar: {
    width: touch.minimum,
    height: touch.minimum,
    borderRadius: radius.full,
    backgroundColor: color.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: {
    fontSize: text.cardTitle.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textInverse,
  },
  profileContent: {
    flex: 1,
  },
  profileName: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textPrimary,
  },
  profileEmail: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
    marginTop: space.xs / 2,
  },

  // Sign out row
  signOutRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touch.listRow,
    paddingVertical: space.sm + space.xs,
    paddingHorizontal: space.md,
    gap: space.sm + space.xs,
    backgroundColor: color.surface,
  },
  signOutText: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.error,
  },
  pressed: {
    backgroundColor: color.pressed,
  },

  // Version
  versionContainer: {
    alignItems: 'center',
    paddingVertical: space.lg,
  },
  versionText: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textMuted,
  },
});
